import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload';
import { faExternalLink } from '@fortawesome/pro-solid-svg-icons/faExternalLink';
import { faX } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from '../../ui/icon';
import { getMsiUrl } from '../../../utils/urls';

interface SalesCopilotModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CopilotOptionCardProps {
  title: React.ReactNode;
  buttonLabel: string;
  href: string;
  isPrimary?: boolean;
  buttonId?: string;
}

function CopilotOptionCard({
  title,
  buttonLabel,
  href,
  isPrimary = false,
  buttonId,
}: CopilotOptionCardProps) {
  return (
    <div className="tw-border tw-rounded-lg tw-p-6 tw-flex tw-flex-col">
      <h3 className="tw-text-lg tw-font-semibold tw-mb-4">{title}</h3>
      <div className="tw-mt-auto">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="tw-block"
        >
          <button
            id={buttonId}
            type="button"
            className={
              isPrimary
                ? 'tw-w-full tw-bg-[#FDE8EF] hover:tw-bg-[#FCD5E3] tw-text-[#C9194A] tw-border-none tw-py-2 tw-px-4 tw-rounded-md tw-font-medium tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-2'
                : 'tw-w-full tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-100 hover:tw-border-gray-300 tw-transition-all tw-py-2 tw-px-4 tw-rounded-md tw-font-medium tw-text-gray-700 tw-flex tw-items-center tw-justify-center tw-gap-2'
            }
          >
            <Icon
              icon={isPrimary ? faDownload : faExternalLink}
              className="tw-w-4 tw-h-4"
            />
            {buttonLabel}
          </button>
        </a>
      </div>
    </div>
  );
}

export function SalesCopilotModal({
  open,
  onOpenChange,
}: SalesCopilotModalProps) {
  const chromeExtensionUrl =
    'https://chromewebstore.google.com/detail/madkudu-copilot/jikgjincjapiebekopikcefbkeaimegj';
  const salesforceInstallUrl =
    'https://help.madkudu.com/docs/madkudu-salesforce-package';
  const salesCopilotWebUrl = getMsiUrl();

  const [isAnimating, setIsAnimating] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(open);

  // Handle enter animation
  useEffect(() => {
    if (open) {
      setShouldRender(true);
      // Trigger animation on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      // Start exit animation
      setIsAnimating(false);
      // Remove from DOM after animation completes
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onOpenChange]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (shouldRender) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [shouldRender]);

  if (!shouldRender) {
    return null;
  }

  const handleOverlayClick = () => {
    onOpenChange(false);
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        opacity: isAnimating ? 1 : 0,
        transition: 'opacity 300ms ease-in-out',
      }}
      onClick={handleOverlayClick}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '900px',
          width: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          position: 'relative',
          zIndex: 10000,
          opacity: isAnimating ? 1 : 0,
          transform: isAnimating ? 'translateY(0)' : 'translateY(10px)',
          transition: 'opacity 300ms ease-in-out, transform 300ms ease-in-out',
        }}
        onClick={handleContentClick}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '24px',
          }}
        >
          <h2
            style={{
              fontSize: '2.25rem',
              fontWeight: 800,
              marginBottom: 0,
              marginTop: 0,
            }}
          >
            Sales Copilot
          </h2>
          <button
            id="sales-copilot-modal-close"
            onClick={() => onOpenChange(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#1f1f1f';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            <Icon icon={faX} className="tw-h-5 tw-w-5" />
          </button>
        </div>
        <div style={{ marginBottom: '32px' }}>
          <p
            style={{ fontSize: '18px', color: '#4b5563', marginBottom: '16px' }}
          >
            Give your sales team the context they need to personalize outreach,
            and get more meetings booked, directly in Salesforce.
          </p>
          <p style={{ fontSize: '18px', color: '#4b5563' }}>
            Sales Copilot surfaces relevant insights in your CRM – such as{' '}
            <span style={{ fontWeight: 600, color: '#111827' }}>
              job openings
            </span>
            ,{' '}
            <span style={{ fontWeight: 600, color: '#111827' }}>
              tech stack
            </span>
            , and{' '}
            <span style={{ fontWeight: 600, color: '#111827' }}>
              recent activity
            </span>{' '}
            within your product or on your website.
          </p>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px',
          }}
        >
          <CopilotOptionCard
            title={
              <>
                Sales Copilot
                <br />
                Chrome Extension
              </>
            }
            buttonLabel="Install Extension"
            href={chromeExtensionUrl}
            isPrimary
            buttonId="sales-copilot-modal-chrome-extension"
          />
          <CopilotOptionCard
            title="Sales Copilot in Salesforce"
            buttonLabel="Install for Salesforce"
            href={salesforceInstallUrl}
            buttonId="sales-copilot-modal-salesforce"
          />
          <CopilotOptionCard
            title="Sales Copilot Web"
            buttonLabel="Go to Sales Copilot"
            href={salesCopilotWebUrl}
            buttonId="sales-copilot-modal-web"
          />
        </div>
      </div>
    </div>
  );

  const portal = createPortal(modalContent, document.body);
  return portal;
}
